import apiClient from "../../../helpers/apiClient";
import {
    createFinalSettlementCheckout,
    sendReservationPaymentLink,
    PAYMENT_METHOD_VALUES,
} from "../../reservations/lib/reservationPayment";
import { getCompanyId } from "../../insurance/lib/insuranceSaleApi";
import { completeReservationCheckOut, registerCheckOutCashPayment } from "../api/checkOutApi";
import endInsuranceCoverageSilently from "../utils/insuranceHelpers";
import { isSolaGateway, sendSolaPaymentLink } from "../../../lib/sola/solaPaymentApi";
import { isSolaPaymentCancelled } from "../../../components/payment/useSolaCardPayment";

export const buildCheckOutSuccessPath = (reservationId, options = {}) => {
    const params = new URLSearchParams();
    params.set("success", "true");
    params.set("reservationProcess", "end");

    if (options.paymentSuccess === true) {
        params.set("paymentSuccess", "true");
    }

    if (options.paymentPlatform) {
        params.set("paymentPlatform", options.paymentPlatform);
    }

    if (options.settlementType) {
        params.set("settlementType", options.settlementType);
    }

    if (typeof options.amount === "number") {
        params.set("amount", String(Math.round(options.amount * 100)));
        params.set("resID", reservationId);
    }

    const query = params.toString();
    return `/reservations/${reservationId}${query ? `?${query}` : ""}`;
};

const buildCheckOutPaymentUrls = ({ reservationId, dueAmount, reservation, companyId }) => {
    const { origin } = window.location;
    const detailsBase = `${origin}/reservations/${reservationId}`;
    const checkOutBase = `${origin}/reservations/${reservationId}/check-out`;

    const successUrl = `${detailsBase}?success=true&resID=${reservationId}&amount=${(dueAmount * 100).toFixed()}&paymentSuccess=true&paymentPlatform=stripe&reservationProcess=end`;
    const cancelUrl = `${checkOutBase}?success=false&paymentSuccess=false&paymentPlatform=stripe`;

    const paymentMetadata = {
        reservationProcess: "end",
        reservationId,
        email: reservation?.email,
        companyId,
    };

    return { successUrl, cancelUrl, paymentMetadata };
};

const persistCheckoutSessionId = async (reservationId, sessionId) => {
    if (!sessionId) return;
    await apiClient.patch(`/api/reservation/session-id/${reservationId}`, { sessionId });
};

const resolveInsuranceReservation = (reservation, insuranceReservation) => insuranceReservation || reservation;

async function completeCheckOutAfterInsurance(insuranceReservation, reservationId, body = {}) {
    await endInsuranceCoverageSilently(insuranceReservation);
    await completeReservationCheckOut(reservationId, body);
}

async function solaCheckOutPayment({
    reservation,
    insuranceReservation,
    dueAmount,
    selectedPayment,
    companyData,
    customerEmail,
    history, toast,
    t,
    requestSolaCardPayment,
}) {
    const reservationId = reservation.id;
    const companyId = getCompanyId(companyData);
    const insuranceSource = resolveInsuranceReservation(reservation, insuranceReservation);

    if (selectedPayment === PAYMENT_METHOD_VALUES.SEND_LINK) {
        await sendSolaPaymentLink({
            reservationId,
            reservationProcess: "end",
            amount: dueAmount,
            customerEmail,
            companyId,
        });
        toast.success({
            message: t("reservationPayment:sendLink.successTitle"),
            description: t("reservationPayment:sendLink.successDescription"),
        });
        history.push(`/reservations/${reservationId}`);
        return;
    }

    try {
        await requestSolaCardPayment({
            reservationId,
            reservationProcess: "end",
            amount: dueAmount,
            customerEmail,
            companyId,
        });
    } catch (error) {
        if (isSolaPaymentCancelled(error)) return;
        throw error;
    }

    await completeCheckOutAfterInsurance(insuranceSource, reservationId);
    history.push(buildCheckOutSuccessPath(reservationId, { paymentSuccess: true, paymentPlatform: "sola" }));
}

export async function endCheckOutPaymentFlow({
    reservation,
    insuranceReservation,
    dueAmount,
    paymentSystem,
    companyData,
    history, toast,
    t,
    requestSolaCardPayment,
}) {
    const reservationId = reservation.id;
    const selectedPayment = paymentSystem || companyData?.defaultPaymentGateway || "stripe";
    const insuranceSource = resolveInsuranceReservation(reservation, insuranceReservation);

    if (selectedPayment === PAYMENT_METHOD_VALUES.CASH) {
        await endInsuranceCoverageSilently(insuranceSource);
        await registerCheckOutCashPayment(reservationId, dueAmount);
        history.push(buildCheckOutSuccessPath(reservationId));
        return;
    }

    const customerEmail = reservation?.email;
    if (!customerEmail) {
        throw new Error("Missing customer email for check-out payment");
    }

    if (isSolaGateway(companyData)) {
        await solaCheckOutPayment({
            reservation,
            insuranceReservation: insuranceSource,
            dueAmount,
            selectedPayment,
            companyData,
            customerEmail,
            history,
toast,
            t,
            requestSolaCardPayment,
        });
        return;
    }

    const companyId = getCompanyId(companyData);
    const { successUrl, cancelUrl, paymentMetadata } = buildCheckOutPaymentUrls({
        reservationId,
        dueAmount,
        reservation,
        companyId,
    });

    const settlement = await createFinalSettlementCheckout({
        reservationId,
        customerEmail,
        successUrl,
        cancelUrl,
        metadata: paymentMetadata,
        companyId,
    });

    const responseStatus = settlement?.status;

    if (responseStatus === "refund") {
        await completeCheckOutAfterInsurance(insuranceSource, reservationId);
        history.push(
            buildCheckOutSuccessPath(reservationId, {
                paymentSuccess: true,
                paymentPlatform: "stripe",
                settlementType: "refund",
            }),
        );
        return;
    }

    if (responseStatus === "full_capture") {
        await completeCheckOutAfterInsurance(insuranceSource, reservationId);
        history.push(
            buildCheckOutSuccessPath(reservationId, {
                paymentSuccess: true,
                paymentPlatform: "stripe",
                settlementType: "full_capture",
            }),
        );
        return;
    }

    if (responseStatus === "checkout") {
        await persistCheckoutSessionId(reservationId, settlement?.sessionId || "");

        const paymentUrl = settlement?.paymentUrl;
        if (!paymentUrl) {
            throw new Error("Payment URL not received");
        }

        if (selectedPayment === PAYMENT_METHOD_VALUES.SEND_LINK) {
            await sendReservationPaymentLink({
                reservationId,
                customerEmail,
                paymentUrl,
                companyId,
            });
            toast.success({
                message: t("reservationPayment:sendLink.successTitle"),
                description: t("reservationPayment:sendLink.successDescription"),
            });
            history.push(`/reservations/${reservationId}`);
            return;
        }

        window.location.href = paymentUrl;
        return;
    }

    throw new Error("Unexpected response from final settlement");
}
